import React, { useEffect, useState, useRef } from 'react';
import { Box, Button, Dialog, Drawer, FormControl, IconButton, Stack, SvgIcon, TextField, Typography, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import InputAdornment from '@mui/material/InputAdornment';

import { useAuth } from 'hooks/useAuthContext';
import { Link } from 'react-router-dom';

import { v4 as uuidv4 } from 'uuid';

import { setApplication } from 'api/application';
import { getResumesData, addSingleJobPreference } from 'api/users';

import { formatCurrency, toTitleCase } from 'utils/Utils';
import { useSnackbar } from 'components/snackbar';

import SelectResumeMobile from './SelectResumeMobile';
import ChatResumeOptions from './ChatResumeOptions';
import SelectResumeOptions from './SelectResumeOptions';
import { useBackNavigationDialog } from 'hooks/useBackNavigationDialog';

const resumeImg = `${process.env.REACT_APP_HOMEPAGE}assets/resume_thumbnail_2.jpg`;

/**
 * ChatBotDrawer is a component that manages the job application chat interface.
 * It handles the display of questions, user responses, and resume selection.
 *
 * Props:
 * - onClose: Function to call when the drawer should be closed.
 * - onSelected: Function called when a resume is selected.
 * - questions: Array of questions for the job application.
 * - tab: Current tab identifier.
 * - data: Job-related data.
 * - candidateDetails: Information about the candidate.
 * - matchingSkills: List of matching skills.
 * - handleCloseDrawer: Function to handle closing the drawer.
 * - isPartTimeJob: Boolean indicating if the job is part-time.
 * - onUpdatePreference: Function to update job preferences.
 */

const ChatBotDrawer = ({ onClose, onSelected, questions, tab, data, candidateDetails, matchingSkills, handleCloseDrawer, isPartTimeJob, onUpdatePreference }) => {

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { jobApply, setJobApply } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  let scrollRef = useRef(null);

  const [formCurrentFixed, setFormCurrentFixed] = useState('');
  const [formCurrentVariable, setFormCurrentVariable] = useState('');
  const [formCurrentPerks, setFormCurrentPerks] = useState('');
  const [formExpectedFixed, setFormExpectedFixed] = useState('');

  const [currentFixed, setCurrentFixed] = useState(Number(jobApply?.currentFixed || candidateDetails?.user?.candidate?.job_preferences?.fixed_ctc || 0));
  const [currentVariable, setCurrentVariable] = useState(Number(jobApply?.currentVariable || candidateDetails?.user?.candidate?.job_preferences?.variable_ctc || 0));
  const [currentPerks, setCurrentPerks] = useState(String(jobApply?.currentPerks || candidateDetails?.user?.candidate?.job_preferences?.other_ctc || ""));
  const [totalCompensation, setTotalCompensation] = useState(Number(jobApply?.totalCurrentCTC ?? candidateDetails?.user?.candidate?.job_preferences?.total_ctc ?? 0));
  const [totalExpectedCompensation, setTotalExpectedCompensation] = useState(Number(jobApply?.totalExpectedCompensation ?? candidateDetails?.user?.candidate?.job_preferences?.expected_ctc ?? 0));
  const [expectedHourlyRate, setExpectedHourlyRate] = useState(Number(jobApply?.hourlyRate || candidateDetails?.user?.candidate?.job_preferences?.hourly_rate || 0));

  const [messages, setMessages] = useState(
    jobApply?.messages || [
      isPartTimeJob
        ? {
          type: 'p-initial',
          id: `initial-options-${uuidv4()}`,
          snapshot: {
            hourlyRate: candidateDetails?.user?.candidate?.job_preferences?.hourly_rate,
          },
          continue: true,
        }
        : {
          type: 'initial',
          id: `initial-options-${uuidv4()}`,
          continue: !!candidateDetails?.user?.candidate?.job_preferences?.expected_ctc,
          continueWithApplicationInitial: !candidateDetails?.user?.candidate?.job_preferences?.expected_ctc,
          count: 0,
          snapshot: {
            totalCurrentCTC: candidateDetails?.user?.candidate?.job_preferences?.total_ctc,
            totalExpectedCTC: candidateDetails?.user?.candidate?.job_preferences?.expected_ctc,
            perks: candidateDetails?.user?.candidate?.job_preferences?.other_ctc
          }
        }
    ]
  );

  const [resumeSelected, setResumeSelected] = useState(null);
  const [showQuestions, setShowQuestions] = useState(false);
  const [answers, setAnswers] = useState(null);
  const [currentAnswer, setCurrentAnswer] = useState('');
  const [chatCompleted, setChatCompleted] = useState(false);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [resumeData, setResumeData] = useState(null);
  // const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showTextField, setShowTextField] = useState(true);
  const [editQusIndex, setEditQusIndex] = useState(null);
  const [sliderIndex, setSliderIndex] = useState(jobApply?.index ?? 0);
  const [isSubmittingCTC, setIsSubmittingCTC] = useState(false);

  const [proceed, setProceed] = useState(false);
  const [updateInProgress, setUpdateInProgress] = useState(false);
  const [continueFlag, setContinueFlag] = useState(jobApply?.continueFlag ?? false);

  const [focusedInput, setFocusedInput] = useState(null);
  const [isFixedError, setIsFixedError] = useState(false);
  const [fixedHelperText, setFixedHelperText] = useState('');

  const [isHourlyRateError, setIsHourlyRateError] = useState(false);
  const [hourlyRateHelperText, setHourlyRateHelperText] = useState('');
  const [textFieldFlag, setTextFieldFlag] = useState(true);
  const [partTimeContinueFlag, setPartTimeContinueFlag] = useState(jobApply?.partTimeContinueFlag ?? false);

  const [disableUpdate, setDisableUpdate] = useState(false);
  const chatEndRef = useRef(null);
  const [inputValue, setInputValue] = useState(expectedHourlyRate.toString());
  const [activeUpdateForm, setActiveUpdateForm] = useState(null);
  const [isSkipClicked, setIsSkipClicked] = useState(false);
  const [chosenResume, setChosenResume] = useState(null);

  const { openDialog, closeDialog, dialogLocation } = useBackNavigationDialog();
  const dialogIds = dialogLocation.state?.dialogIds || [];

  const selectResumeDialogOpen = dialogIds.includes("updateSelectResumeDialog");

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [answers, resumeData, showQuestions, chatCompleted, messages, proceed]);

  useEffect(() => {
    if (selectResumeDialogOpen) localStorage.setItem('openChatBot', true);
    else localStorage.removeItem('openChatBot');
  }, [selectResumeDialogOpen]);

  useEffect(() => {
    console.log(messages);
  }, [messages]);

  useEffect(() => {
    const lastMessage = messages[messages.length - 1];

    if (lastMessage?.type === 'update-form' && lastMessage.updateInProgress) {
      const formType = lastMessage.updateType;
      setActiveUpdateForm(formType);
      setUpdateInProgress(true);
      if (formType === 'current') {
        setFormCurrentFixed(String(currentFixed));
        setFormCurrentVariable(String(currentVariable));
        setFormCurrentPerks(currentPerks);
      } else if (formType === 'expected') {
        setFormExpectedFixed(String(totalExpectedCompensation));
      }
    } else {
      setActiveUpdateForm(null);
      setUpdateInProgress(false);
    }
  }, [messages]);

  const lastUpdateFormMessage = [...messages]
    .reverse()
    .find((msg) =>
      ['update-form', 'partTime-update-form'].includes(msg.type)
    );

  useEffect(() => {
    if (lastUpdateFormMessage && lastUpdateFormMessage.updateInProgress === true) {
      const el = document.getElementById(`message-${lastUpdateFormMessage.id}`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'end' });
      }
    }
  }, [lastUpdateFormMessage]);

  /**
   * Handles changes to the hourly rate input. It removes any commas from the input, and
   * only allows numbers. If the input is invalid, it will keep the previous valid value.
   * If the input is valid, it will update the input value and clear any error messages.
   * @param {React.ChangeEvent<HTMLInputElement>} e - The change event.
   */
  const handleHourlyRateChange = (e) => {
    const raw = e.target.value.replace(/,/g, '');

    if (/^\d*$/.test(raw)) {
      setInputValue(raw);
      if (isHourlyRateError) {
        setIsHourlyRateError(false);
        setHourlyRateHelperText('');
      }
    }
  };

  /**
   * Fetches resume data from the API and stores it in the component's state.
   * It also checks if the user has added a resume, and if not, it shows an error
   * message and closes the drawer. If the jobApply object is present, it sets the
   * slider index, chosen resume, and open state according to the jobApply object.
   * If the jobApply object has a resume, it sets the resume selected state to true.
   * If the jobApply object has answers, it sets the answers state and sets the
   * current question index based on the length of the answers object. If the
   * length of the answers object is equal to the length of the questions array,
   * it sets the chat completed state to true and hides the questions and text field.
   */
  const fetchData = async () => {
    try {
      const response = await getResumesData();
      if (response?.success && response?.data) {
        const uploadedResume = response?.data?.filter(item => item?.link) || [];

        const sortedResumes = [
          ...uploadedResume,
          ...response?.data?.filter(item => !uploadedResume.some(res => res._id === item._id)) || []
        ];

        setResumeData(sortedResumes);
        setTotalExpectedCompensation(jobApply?.totalExpectedCompensation ?? candidateDetails?.user?.candidate?.job_preferences?.expected_ctc)
        setTotalCompensation(jobApply?.totalCurrentCTC ?? candidateDetails?.user?.candidate?.job_preferences?.total_ctc)
        setDisableUpdate(jobApply?.disableUpdate);
      } else {
        onClose();
        enqueueSnackbar('Please check that you have added your resume.', { variant: 'error' });
      }
    } finally {
      setLoading(false);
      setTotalExpectedCompensation(jobApply?.totalExpectedCompensation ?? candidateDetails?.user?.candidate?.job_preferences?.expected_ctc)
      setTotalCompensation(jobApply?.totalCurrentCTC ?? candidateDetails?.user?.candidate?.job_preferences?.total_ctc)
      setDisableUpdate(jobApply?.disableUpdate);
      if (jobApply && jobApply?.job?._id === data?._id) {
        if (jobApply?.openSelectResume) {
          setSliderIndex(jobApply?.index);
          setChosenResume(jobApply?.chosenResume ? jobApply?.chosenResume : jobApply?.resume);
          jobApply?.openSelectResume ? handleOpenSelectResumeDialog() : handleCloseSelectResumeDialog();
        }
        if (jobApply?.resume) {
          setResumeSelected(jobApply?.resume);
        } else {
          return;
        }

        if (questions && questions?.length > 0) {
          setShowQuestions(jobApply?.resume ? true : false);
        } else {
          setChatCompleted(true);
          setShowQuestions(false);
          setShowTextField(false);
        }
        if (jobApply?.answer && Object.keys(jobApply?.answer)?.length > 0) {
          setAnswers(jobApply?.answer);
          if (Object.keys(jobApply?.answer)?.length < questions?.length) {
            const index = Object.keys(jobApply?.answer)?.length;
            setCurrentQuestionIndex(index);
          } else {
            setChatCompleted(true);
            setShowQuestions(false);
            setShowTextField(false);
          }
        }
      }
    }
  };

  /**
   * Handles user input for expected CTC and updates the chatbot messages.
   *
   * @param {string} number - The user-provided expected CTC amount.
   * @returns {undefined}
   */
  const handleSetExpectedCTC = (number) => {
    if (number === '' || number === undefined || number === null || number?.length === 0 || number === '0') {
      enqueueSnackbar('Enter a valid amount.', { variant: 'error' });
      return;
    }
    setIsSubmittingCTC(true);

    const totalExpectedCtcTemp = Number(number) || 0;

    updateValues({ expected_ctc: totalExpectedCtcTemp });

    const updatedPrevMessages = messages.map(msg =>
      msg.type === 'updated-p-options' || msg.type === 'p-initial' || msg.type === 'updated-options' || msg.type === 'initial'
        ? { ...msg, continue: false, linkdisabled: true }
        : msg
    );

    const newMessages = [
      {
        type: 'user-expected-ctc',
        updateType: 'expected',
        id: `user-request-expectedCTC-${Date.now()}`,
        value: number
      },
      {
        type: 'updated-options',
        updateType: 'expected',
        id: `updated-options-${Date.now()}`,
        snapshot: {
          totalCurrentCTC: totalCompensation,
          totalExpectedCTC: totalExpectedCtcTemp,
          perks: currentPerks
        },
        continue: true,
      }
    ];
    setTotalExpectedCompensation(totalExpectedCtcTemp);
    setMessages([...updatedPrevMessages, ...newMessages]);

    setJobApply(prev => ({
      ...prev,
      job: data ?? prev?.job,
      openChatBot: true,
      resume: resumeSelected ?? prev?.resume,
      answer: {
        ...(prev?.answer || {}),
        [questions[currentQuestionIndex]]: currentAnswer
      },
      messages: [...(updatedPrevMessages || []), ...newMessages],
      totalExpectedCompensation: totalExpectedCtcTemp,
      totalCurrentCTC: totalCompensation,
      currentFixed: currentFixed,
      currentVariable: currentVariable,
      currentPerks: currentPerks,
      disableUpdate: disableUpdate
    }));

    setCurrentAnswer('');
  };

  /**
   * Handles user click on part time job preference.
   * @param {boolean} expectedHourlyRate - The user-provided expected hourly rate.
   * @returns {undefined}
   */
  const handlePartTimeClick = () => {
    setUpdateInProgress(true);
    const timestamp = uuidv4();

    const updatedPrevMessages = messages.map(msg =>
      msg.type === 'updated-p-options' || msg.type === 'p-initial'
        ? { ...msg, continue: false, linkdisabled: true }
        : msg
    );

    const newMessages = [
      {
        type: 'partTime-update-request',
        id: `user-request-${timestamp}`,
        hourlyRate: expectedHourlyRate,
      },
      {
        type: 'partTime-confirmation',
        id: `bot-confirm-${timestamp}`
      },
      {
        type: 'partTime-update-form',
        id: `form-${timestamp}`,
        updateInProgress: true,
      }
    ];
    setMessages([...updatedPrevMessages, ...newMessages]);
    setJobApply(prev => ({
      ...prev,
      job: data ?? prev?.job,
      openChatBot: true,
      resume: resumeSelected ?? prev?.resume,
      answer: prev?.answer,
      messages: [...updatedPrevMessages, ...newMessages],
      hourlyRate: expectedHourlyRate,
      disableUpdate: disableUpdate,
      partTimeContinueFlag: false,
    }));
    setPartTimeContinueFlag(false);

  }

  /**
   * Handles the update click event by generating a series of new chat messages 
   * based on the provided update type. This function creates a snapshot of the 
   * current and expected total compensation details and updates the state with 
   * new messages reflecting the update request and form. It also modifies 
   * existing messages to ensure continuity and disables certain options. 
   * Additionally, it updates the job application context with the latest 
   * job data and user-provided answers.
   *
   * @param {string} type - The type of update being requested (e.g., salary, position).
   */

  const handleUpdateClick = (type) => {
    const snapshot = {
      totalCurrentCTC: totalCompensation,
      totalExpectedCTC: totalExpectedCompensation,
      perks: currentPerks
    };

    const newMessages = [
      { type: 'user-update-request', updateType: type, id: uuidv4(), snapshot },
      { type: 'bot-confirmation', updateType: type, id: uuidv4() },
      { type: 'update-form', updateType: type, id: uuidv4(), updateInProgress: true, activeUpdateForm: type }
    ];

    const updatedPrevMessages = messages.map(msg =>
      (msg.type === 'updated-options' || msg.type === 'initial')
        ? { ...msg, continue: false, linkdisabled: true }
        : msg
    );
    const finalMessages = [...updatedPrevMessages, ...newMessages];
    setMessages(finalMessages);

    setJobApply(prev => ({
      ...prev,
      job: data ?? prev?.job,
      openChatBot: true,
      resume: resumeSelected ?? prev?.resume,
      answer: prev?.answer,
      messages: [...updatedPrevMessages, ...newMessages],
      totalExpectedCompensation: totalExpectedCompensation,
      totalCurrentCTC: totalCompensation,
      currentFixed: currentFixed,
      currentVariable: currentVariable,
      currentPerks: currentPerks,
      disableUpdate: disableUpdate,
      continueFlag: false,
    }));
    setContinueFlag(false);
  };
  /**
   * Handles the submission of the part time job preference hourly rate input.
   * @param {{ value: string }} - The input value from the part time job preference form.
   * @returns {Promise<void>}
   * @description
   * - Validate the input value.
   * - If valid, update the state with new messages reflecting the updated
   *   part time job preference and form. It also modifies existing messages to ensure
   *   continuity and disables certain options.
   * - Update the job application context with the latest job data and user-provided answers.
   * - Call the updateValues function to update the user's hourly rate in the database.
   */
  const handlePartTimeSubmit = async ({ value }) => {

    const cleaned = parseInt(inputValue.replace(/,/g, ''), 10);
    if (cleaned > 500000) {
      enqueueSnackbar('Hourly rate must not exceed 5 lakh', { variant: 'error' });
      return;
    }

    if (isNaN(cleaned) || cleaned === 0) {
      setIsHourlyRateError(true);
      setHourlyRateHelperText(
        isNaN(cleaned) ? 'Hourly rate cannot be empty' : 'Hourly rate cannot be zero'
      );
      return;
    }


    setIsHourlyRateError(false);
    setHourlyRateHelperText('');
    setExpectedHourlyRate(cleaned);
    setUpdateInProgress(false);

    const timestamp = uuidv4();

    const newMessages = [
      {
        type: 'updated-p-options',
        id: `updated-options-${timestamp}`,
        continue: true,
        snapshot: {
          hourlyRate: expectedHourlyRate,
        },
      },
    ];

    /**
     * This function returns an array of messages where the last 'partTime-update-form' message
     * has its updateInProgress property set to false and its hourlyRate updated to the latest
     * expectedHourlyRate. Additionally, it adds the newMessages to the end of the array.
     * @returns {Array<Object>} - The array of messages.
    */
    const prevMessages = () => {
      let found = false;
      const reversed = [...messages].reverse();

      const updatedReversed = reversed.map((message) => {
        if (!found && message.type === 'partTime-update-form') {
          found = true;
          return {
            ...message,
            updateInProgress: false,
            hourlyRate: expectedHourlyRate,
          };
        }
        return message;
      });

      return updatedReversed.reverse().concat(newMessages);
    };
    const updatedMessages = prevMessages();
    setMessages(updatedMessages);
    setJobApply(prev => ({
      ...prev,
      job: data ?? prev?.job,
      openChatBot: true,
      resume: resumeSelected ?? prev?.resume,
      answer: prev?.answer,
      messages: updatedMessages,
      hourlyRate: expectedHourlyRate,
      disableUpdate: disableUpdate,
      partTimeContinueFlag: false,
      continueFlag: false
    }));
    setContinueFlag(false);
    setPartTimeContinueFlag(false);
    updateValues({ hourly_rate: Number(expectedHourlyRate) });

  };

  /**
   * Handles the submission of the current/expected compensation form.
   * @description
   * - Validate the input value.
   * - If valid, update the state with new messages reflecting the updated
   *   compensation and form. It also modifies existing messages to ensure
   *   continuity and disables certain options.
   * - Update the job application context with the latest job data and user-provided answers.
   * - Call the updateValues function to update the user's compensation in the database.
   */
  const handleUpdateSubmit = async () => {
    const isCurrentForm = activeUpdateForm === 'current';
    const valueToValidate = isCurrentForm ? formCurrentFixed : formExpectedFixed;
    const fixedNumber = parseFloat(valueToValidate.replace(/,/g, ''));

    if (isNaN(fixedNumber) || fixedNumber === 0) {
      setIsFixedError(true);
      setFixedHelperText("Fixed compensation cannot be zero.");
      return;
    }
    setIsFixedError(false);
    setFixedHelperText('');

    const nextState = {
      totalCurrentCTC: totalCompensation,
      totalExpectedCTC: totalExpectedCompensation,
      currentFixed: currentFixed,
      currentVariable: currentVariable,
      currentPerks: currentPerks,
    };

    if (isCurrentForm) {
      const variableNumber = parseFloat(formCurrentVariable.replace(/,/g, '')) || 0;

      nextState.currentFixed = fixedNumber;
      nextState.currentVariable = variableNumber;
      nextState.currentPerks = formCurrentPerks;
      nextState.totalCurrentCTC = fixedNumber + variableNumber;
    } else {
      nextState.totalExpectedCTC = fixedNumber;
    }

    setTotalCompensation(nextState.totalCurrentCTC);
    setTotalExpectedCompensation(nextState.totalExpectedCTC);
    setCurrentFixed(nextState.currentFixed);
    setCurrentVariable(nextState.currentVariable);
    setCurrentPerks(nextState.currentPerks);

    const confirmationSnapshot = {
      totalCurrentCTC: nextState.totalCurrentCTC,
      totalExpectedCTC: nextState.totalExpectedCTC,
      perks: nextState.currentPerks
    };

    const formSubmissionSnapshot = {
      fixed: valueToValidate,
      variable: isCurrentForm ? formCurrentVariable : '',
      perks: isCurrentForm ? formCurrentPerks : '',
    };

    let formUpdated = false;
    const reversedMessages = [...messages].reverse().map(message => {
      if (!formUpdated && message.type === 'update-form' && message.updateType === activeUpdateForm) {
        formUpdated = true;
        return {
          ...message,
          updateInProgress: false,
          snapshot: formSubmissionSnapshot,
        };
      }
      return message;
    });

    const messagesWithUpdatedForm = reversedMessages.reverse();

    const finalNewMessages = [
      ...messagesWithUpdatedForm,
      {
        type: 'updated-options',
        id: `updated-options-${uuidv4()}`,
        continue: true,
        snapshot: confirmationSnapshot,
      }
    ];

    setMessages(finalNewMessages);

    setJobApply(prev => ({
      ...prev,
      job: data ?? prev?.job,
      openChatBot: true,
      resume: resumeSelected ?? prev?.resume,
      answer: prev?.answer,
      messages: finalNewMessages,
      totalCurrentCTC: nextState.totalCurrentCTC,
      totalExpectedCompensation: nextState.totalExpectedCTC,
      currentFixed: nextState.currentFixed,
      currentVariable: nextState.currentVariable,
      currentPerks: nextState.currentPerks,
      continueFlag: false,
      partTimeContinueFlag: false,
    }));

    updateValues({
      total_ctc: nextState.totalCurrentCTC,
      expected_ctc: nextState.totalExpectedCTC,
      fixed_ctc: nextState.currentFixed,
      variable_ctc: nextState.currentVariable,
      other_ctc: nextState.currentPerks
    });
    setContinueFlag(false);
    setPartTimeContinueFlag(false);

    setUpdateInProgress(false);
    setActiveUpdateForm(null);
    setTextFieldFlag(true);
  };

  const handlePartTimeProceed = () => {
    const confirmationMessage = {
      type: 'confirmation-part-time',
      id: `confirmation-${uuidv4()}`,
      snapshot: {
        hourlyRate: expectedHourlyRate,
      }
    };

    const updatedMessages = [...messages, confirmationMessage];
    setMessages(updatedMessages);
    setDisableUpdate(true);
    setProceed(true);
    setJobApply(prev => ({
      ...prev,
      job: data ?? prev?.job,
      openChatBot: true,
      resume: resumeSelected ?? prev?.resume,
      answer: prev?.answer,
      messages: updatedMessages,
      disableUpdate: true,
      partTimeContinueFlag: true
    }));
    setPartTimeContinueFlag(true);
  }
  /**
   * Handles the proceed action by generating a confirmation message with the current
   * and expected total compensation. Updates the messages state with the new confirmation
   * message and sets various application states including proceed and disableUpdate flags.
   * It also updates job application data with new compensation details and the current answer
   * to the questions.
  *
  * @param {number} totalExpectedCTC - The expected total compensation.
  */

  const handleProceed = (totalExpectedCTC) => {
    const confirmationMessage = {
      type: 'confirmation',
      id: `confirmation-${uuidv4()}`,
      snapshot: {
        current: totalCompensation,
        expected: totalExpectedCTC,
        perks: currentPerks
      }
    };
    const updatedMessages = [...messages, confirmationMessage];
    setMessages(updatedMessages);
    setProceed(true);
    setDisableUpdate(true);

    setJobApply(prev => ({
      ...prev,
      job: data ?? prev?.job,
      openChatBot: true,
      resume: resumeSelected ?? prev?.resume,
      answer: {
        ...(prev?.answer || {}),
        [questions[currentQuestionIndex]]: currentAnswer
      },
      messages: updatedMessages,
      totalExpectedCompensation: totalExpectedCompensation,
      totalCurrentCTC: totalCompensation,
      currentFixed: currentFixed,
      currentVariable: currentVariable,
      currentPerks: currentPerks,
      disableUpdate: true,
      continueFlag: true,
    }));
    setContinueFlag(true);
  };

  /**
   * Handles the selection of a resume within the chat interface.
   * Updates the application state with the selected resume, slider index,
   * and job application data. It determines whether to show questions
   * based on the availability of questions, and updates the chat completion
   * status accordingly.
   *
   * @param {Object} item - The selected resume item.
   * @param {number} index - The index of the selected resume.
   */

  const handleSelectResumeInChat = (item, index) => {
    setSliderIndex(index);
    setResumeSelected(item);
    setChosenResume(item);
    setJobApply({
      job: data,
      openChatBot: true,
      resume: item,
      answer: answers,
      messages: messages,
      totalExpectedCompensation: totalExpectedCompensation,
      totalCurrentCTC: totalCompensation,
      currentFixed: currentFixed,
      currentVariable: currentVariable,
      currentPerks: currentPerks,
      disableUpdate: disableUpdate
    });

    if (questions && questions?.length > 0 && !chatCompleted) {
      setShowQuestions(true);
    } else {
      setShowQuestions(false);
      setChatCompleted(true);
      setShowTextField(false);
    }
  };

  /**
   * Handles the selection of a resume within the dialog interface.
   * Updates the application state with the selected resume, slider index,
   * and job application data. It determines whether to show questions
   * based on the availability of questions, and updates the chat completion
   * status accordingly.
   *
   * @param {Object} resume - The selected resume item.
   * @param {number} index - The index of the selected resume.
   */
  const handleSelectResumeFromDialog = (resume, index) => {
    setResumeSelected(resume);
    setChosenResume(resume);
    setJobApply({
      job: data,
      openChatBot: true,
      resume: resume,
      answer: answers,
      messages: messages,
      totalExpectedCompensation: totalExpectedCompensation,
      totalCurrentCTC: totalCompensation,
      currentFixed: currentFixed,
      currentVariable: currentVariable,
      currentPerks: currentPerks,
      disableUpdate: disableUpdate
    });
    setSliderIndex(index);
    if (questions && questions?.length > 0 && !chatCompleted) {
      setShowQuestions(true);
    }
    handleClosed();
  };

  /**
   * Handles input change event for the chat interface input field.
   * Checks if the chat is asking for initial CTC and if so, validates
   * the input to be numeric and shows an error if it is not. If the
   * chat is not asking for initial CTC, it validates the input to have
   * a maximum of 30 words and shows an error if it exceeds the limit.
   * If the input is valid, it updates the current answer state with the
   * new value.
   * @param {Event} e - Input change event.
   */
  const handleInputChange = (e) => {
    const value = e.target.value;
    const isAskingForInitialCTC = !isPartTimeJob && !resumeSelected;

    if (isAskingForInitialCTC) {
      if (updateInProgress) {
        enqueueSnackbar('You can’t update right now!', { variant: 'error' });
        return;
      }

      if (!/^\d{0,9}(\.\d{0,2})?$/.test(value)) {
        enqueueSnackbar('Only numeric values up to 9 digits are allowed!', { variant: 'error' });
        return;
      }

    } else {
      const wordCount = value.trim().split(/\s+/).filter(Boolean).length;
      if (wordCount > 30) {
        enqueueSnackbar('Maximum 30 words allowed!', { variant: 'error' });
        return;
      }
    }

    setCurrentAnswer(value);

  };

  /**
   * Updates job preference values for CTC by calling the addSingleJobPreference API.
   * Logs a success message and calls onUpdatePreference if the update is successful.
   * Logs a failure message if the update fails.
   *
   * @param {Object} object - The object containing the job preference values to update.
   * @returns {Promise<void>} - A promise that resolves when the operation is complete.
   */

  const updateValues = async (object) => {
    const response = await addSingleJobPreference(object);
    if (response?.success) {
      console.log('updating preference', response?.data);
      console.log("sucessfully updated values for ctc");
      onUpdatePreference();
    }
    else {
      console.log("failed to update values for ctc");
    }
  }

  /**
   * Handles setting the answer for the current or edited question.
   * 
   * If the current question index is not zero and the current answer is empty,
   * shows an error notification. If an edited question index is provided,
   * updates the answer for the edited question and resets relevant states.
   * Otherwise, sets the answer for the current question and updates the job
   * application state accordingly.
   * 
   * After setting the answer, it clears the current answer state and,
   * if applicable, increments the current question index. If all questions
   * are answered, it marks the chat as completed.
   * 
   * @param {string} answerToSet - The answer to be set for the current or edited question.
   */

  const handleSetAnswer = (answerToSet) => {
    if (currentQuestionIndex != 0 && currentAnswer === '') {
      enqueueSnackbar('Please write your answer.', { variant: 'error' });
    } else if (editQusIndex !== null) {
      setAnswers((prevAnswers) => ({
        ...prevAnswers,
        [questions[editQusIndex]]: answerToSet,
      }));
      setJobApply({
        job: data,
        openChatBot: true,
        resume: resumeSelected,
        answer: { ...answers, [questions[editQusIndex]]: answerToSet },
        messages: messages,
        totalExpectedCompensation: totalExpectedCompensation,
        totalCurrentCTC: totalCompensation,
        currentFixed: currentFixed,
        currentVariable: currentVariable,
        currentPerks: currentPerks,
        disableUpdate: disableUpdate
      });
      setCurrentAnswer('');
      if (chatCompleted) {
        setShowTextField(false);
      }
      setEditQusIndex(null);
    } else {
      setAnswers((prevAnswers) => ({
        ...prevAnswers,
        [questions[currentQuestionIndex]]: answerToSet,
      }));
      setJobApply({
        job: data,
        openChatBot: true,
        resume: resumeSelected,
        answer: { ...answers, [questions[currentQuestionIndex]]: answerToSet },
        messages: messages,
        totalExpectedCompensation: totalExpectedCompensation,
        totalCurrentCTC: totalCompensation,
        currentFixed: currentFixed,
        currentVariable: currentVariable,
        currentPerks: currentPerks,
        disableUpdate: disableUpdate
      });
      setCurrentAnswer('');
      if (questions && questions?.length > currentQuestionIndex + 1) {
        setCurrentQuestionIndex((prevIndex) => prevIndex + 1);
      } else {
        setShowQuestions(false);
        setChatCompleted(true);
        setShowTextField(false);
      }
    }
  };
  /**
   * Renders a view for the chatbot to show the current and expected CTC
   * details to the user and to allow the user to update the values.
   * @param {Object} snapshot - The snapshot object containing the current
   * and expected CTC values.
   * @param {Object} message - The message object containing the question text.
   * @returns {JSX.Element} - The JSX element representing the view.
   */
  const renderOptionsView = (snapshot, message) => (
    <Box className="chat-row message-in" sx={{ mt: 2 }}>
      <Stack direction="row" spacing={1}>
        <SvgIcon className="jn-chat-icon">
          <svg xmlns="http://www.w3.org/2000/svg" width="26" height="28" viewBox="0 0 26 28" fill="none">
            <path d="M10.4 25.8375L12.1555 26.809C12.7873 27.1737 13.5565 27.1545 14.1824 26.7999L18.396 24.3892C18.4641 24.3443 18.5504 24.2677 18.6184 24.2228L24.0025 21.098C24.6284 20.7434 25.0296 20.0484 25.0237 19.3291L25.0116 7.82422C25.0058 7.10486 24.6062 6.41089 23.9745 6.04615L16.046 1.46864L16.0144 1.45041L14.0244 0.301469C13.3926 -0.0632728 12.5919 -0.0623187 11.966 0.2923L1.9964 6.03424C1.37049 6.38886 0.969277 7.08379 0.975122 7.80315L0.966118 16.9373C0.969695 17.1499 0.991509 17.331 1.04491 17.5303L1.05041 19.3445C1.05625 20.0638 1.45581 20.7578 2.08756 21.1226L9.98445 25.6818C10.129 25.7232 10.2554 25.7961 10.4 25.8375Z" fill="#3D1C9B" />
            <path fill-rule="evenodd" clip-rule="evenodd" d="M8.09204 14.572L6.79785 16.1014C7.38612 16.7838 8.30381 17.1602 9.43328 17.1367C11.3157 17.1367 12.3511 16.1955 12.3511 14.2426V9.88965H9.99802V11.7014V14.4073C9.99802 14.972 9.71565 15.2779 9.22151 15.2779C8.82149 15.2779 8.46853 15.0426 8.09204 14.572ZM19.1283 17.0209V13.8679C19.1283 12.1267 18.14 11.3738 16.8693 11.3738C16.2105 11.3738 15.6457 11.5856 15.1987 12.0326V11.4914H13.0338V16.9973H15.3163V14.4091C15.3163 13.5856 15.6928 13.3032 16.1634 13.3032C16.587 13.3032 16.8458 13.562 16.8458 14.2444V17.0209H19.1283Z" fill="white" />
          </svg>
        </SvgIcon>
        <Box className="message-in-text-container">
          <Typography mb={1} className="msg-text">Please confirm the following details:</Typography>

          <Stack mb={1} spacing={0.5}>
            <Typography className="msg-text">Your Current CTC (Fixed + Variable)</Typography>
            <Typography className="msg-text ctc-amt">
              ₹{formatCurrency(snapshot?.totalCurrentCTC)}
            </Typography>
            {(snapshot?.perks) && <Typography className="msg-text" sx={{ color: '#6B7280!important', lineHeight: '18px' }}>
              {`Perks- ${snapshot?.perks}`}
            </Typography>}
            <span
              underline="none"
              className={(message?.linkdisabled || updateInProgress) ? "disable-link" : "update-link"}
              disabled={message?.linkdisabled || updateInProgress}
              onClick={() => !updateInProgress && handleUpdateClick('current')}
            >
              Update
            </span>
          </Stack>

          {(message?.linkdisabled && snapshot?.totalExpectedCTC) ||
            (!message?.linkdisabled && totalExpectedCompensation && snapshot?.totalExpectedCTC) ? (
            <Stack mb={1} spacing={0.5}>
              <Typography className="msg-text">Your Expected CTC (Fixed + Variable)</Typography>
              <Typography className="msg-text ctc-amt">
                ₹{formatCurrency(snapshot?.totalExpectedCTC)}
              </Typography>
              <span
                underline="none"
                className={(message?.linkdisabled || updateInProgress) ? "disable-link" : "update-link"}
                disabled={message?.linkdisabled || updateInProgress}
                onClick={() => !updateInProgress && handleUpdateClick('expected')}
              >
                Update
              </span>
            </Stack>
          ) : <Typography className="msg-text" sx={{ color: '#6B7280!important', lineHeight: '18px' }}>To update your current CTC , please click on "Update" button above</Typography>
          }
        </Box>
      </Stack>
    </Box>
  );

  /**
   * Renders a message asking the user to confirm their expected hourly rate.
   * @param {object} snapshot - The snapshot of the current job application.
   * @param {object} message - The message object from the conversation.
   * @returns {React.ReactElement} - The React element to render.
   */
  const renderPartTime = (snapshot, message) => (
    <Box className="chat-row message-in" sx={{ mt: 2 }}>
      <Stack direction="row" spacing={1}>
        <SvgIcon className="jn-chat-icon">
          <svg xmlns="http://www.w3.org/2000/svg" width="26" height="28" viewBox="0 0 26 28" fill="none">
            <path d="M10.4 25.8375L12.1555 26.809C12.7873 27.1737 13.5565 27.1545 14.1824 26.7999L18.396 24.3892C18.4641 24.3443 18.5504 24.2677 18.6184 24.2228L24.0025 21.098C24.6284 20.7434 25.0296 20.0484 25.0237 19.3291L25.0116 7.82422C25.0058 7.10486 24.6062 6.41089 23.9745 6.04615L16.046 1.46864L16.0144 1.45041L14.0244 0.301469C13.3926 -0.0632728 12.5919 -0.0623187 11.966 0.2923L1.9964 6.03424C1.37049 6.38886 0.969277 7.08379 0.975122 7.80315L0.966118 16.9373C0.969695 17.1499 0.991509 17.331 1.04491 17.5303L1.05041 19.3445C1.05625 20.0638 1.45581 20.7578 2.08756 21.1226L9.98445 25.6818C10.129 25.7232 10.2554 25.7961 10.4 25.8375Z" fill="#3D1C9B" />
            <path fill-rule="evenodd" clip-rule="evenodd" d="M8.09204 14.572L6.79785 16.1014C7.38612 16.7838 8.30381 17.1602 9.43328 17.1367C11.3157 17.1367 12.3511 16.1955 12.3511 14.2426V9.88965H9.99802V11.7014V14.4073C9.99802 14.972 9.71565 15.2779 9.22151 15.2779C8.82149 15.2779 8.46853 15.0426 8.09204 14.572ZM19.1283 17.0209V13.8679C19.1283 12.1267 18.14 11.3738 16.8693 11.3738C16.2105 11.3738 15.6457 11.5856 15.1987 12.0326V11.4914H13.0338V16.9973H15.3163V14.4091C15.3163 13.5856 15.6928 13.3032 16.1634 13.3032C16.587 13.3032 16.8458 13.562 16.8458 14.2444V17.0209H19.1283Z" fill="white" />
          </svg>
        </SvgIcon>
        <Box className="message-in-text-container">
          <Typography mb={1} className="msg-text">Please confirm the following details:</Typography>

          <Stack mb={1} spacing={0.5}>
            <Typography className="msg-text">Your Expected Hourly Rate</Typography>
            <Typography className="msg-text ctc-amt">
              ₹{formatCurrency(snapshot?.hourlyRate)} / hour
            </Typography>
            <span
              underline="none"
              disabled={message?.linkdisabled || updateInProgress}
              className={(message?.linkdisabled || updateInProgress) ? "disable-link" : "update-link"}
              onClick={(e) => {
                if (!updateInProgress) {
                  e.preventDefault();
                  handlePartTimeClick();
                }
              }}
            >
              Update
            </span>
          </Stack>
        </Box>
      </Stack>
    </Box>
  );

  /**
   * A reusable React component for a Job Network icon. The icon is used to represent the
   * Job Network feature in the chatbot. The icon is a purple and white logo of the
   * Job Network.
   * @returns {React.ReactElement} - The React element to render.
   */
  const JobNetworkIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="26" height="28" viewBox="0 0 26 28" fill="none">
      <path d="M10.4 25.8375L12.1555 26.809C12.7873 27.1737 13.5565 27.1545 14.1824 26.7999L18.396 24.3892C18.4641 24.3443 18.5504 24.2677 18.6184 24.2228L24.0025 21.098C24.6284 20.7434 25.0296 20.0484 25.0237 19.3291L25.0116 7.82422C25.0058 7.10486 24.6062 6.41089 23.9745 6.04615L16.046 1.46864L16.0144 1.45041L14.0244 0.301469C13.3926 -0.0632728 12.5919 -0.0623187 11.966 0.2923L1.9964 6.03424C1.37049 6.38886 0.969277 7.08379 0.975122 7.80315L0.966118 16.9373C0.969695 17.1499 0.991509 17.331 1.04491 17.5303L1.05041 19.3445C1.05625 20.0638 1.45581 20.7578 2.08756 21.1226L9.98445 25.6818C10.129 25.7232 10.2554 25.7961 10.4 25.8375Z" fill="#3D1C9B" />
      <path fillRule="evenodd" clipRule="evenodd" d="M8.09204 14.572L6.79785 16.1014C7.38612 16.7838 8.30381 17.1602 9.43328 17.1367C11.3157 17.1367 12.3511 16.1955 12.3511 14.2426V9.88965H9.99802V11.7014V14.4073C9.99802 14.972 9.71565 15.2779 9.22151 15.2779C8.82149 15.2779 8.46853 15.0426 8.09204 14.572ZM19.1283 17.0209V13.8679C19.1283 12.1267 18.14 11.3738 16.8693 11.3738C16.2105 11.3738 15.6457 11.5856 15.1987 12.0326V11.4914H13.0338V16.9973H15.3163V14.4091C15.3163 13.5856 15.6928 13.3032 16.1634 13.3032C16.587 13.3032 16.8458 13.562 16.8458 14.2444V17.0209H19.1283Z" fill="white" />
    </svg>
  );

  /**
   * Opens the modal to select a resume for job application.
   * @function
   * @since v1.0.0
   */
  const handleChangeResume = () => {
    handleOpenSelectResumeDialog();
  };

  /**
   * Handles the closing of the modal for selecting a resume for job application.
   * @function
   * @since v1.0.0
   */
  const handleClosed = () => {
    handleCloseSelectResumeDialog();
    setJobApply((prev) => ({ ...prev, openSelectResume: false }));
  };

  /**
   * Submits the job application based on the provided information.
   * @async
   * @function
   * @since v1.0.0
   * @param {object} candidateDetails - The candidate details to be updated.
   * @param {object} matchingSkills - The matching skills to be updated.
   * @param {object} data - The job details to be updated.
   * @param {object} resumeSelected - The selected resume to be used for the job application.
   * @param {number} totalCompensation - The total compensation of the job preference.
   * @param {number} currentFixed - The current fixed compensation of the job preference.
   * @param {number} currentVariable - The current variable compensation of the job preference.
   * @param {string} currentPerks - The current perks of the job preference.
   * @param {number} totalExpectedCompensation - The total expected compensation of the job preference.
   * @param {boolean} isSubmitting - The flag to track if the application is being submitted.
   * @param {object} answers - The answers to the job application questions.
   * @param {function} onClose - The function to close the job application drawer.
   * @param {function} onSelected - The function to update the selected resume.
   * @returns {Promise<void>}
   */
  const onSubmit = async () => {
    try {
      setIsSubmitting(true);
      if (candidateDetails && matchingSkills && data && resumeSelected) {
        candidateDetails.user.candidate.job_preferences.total_ctc = Number(totalCompensation);
        candidateDetails.user.candidate.job_preferences.fixed_ctc = Number(currentFixed);
        candidateDetails.user.candidate.job_preferences.variable_ctc = Number(currentVariable);
        candidateDetails.user.candidate.job_preferences.other_ctc = currentPerks;
        candidateDetails.user.candidate.job_preferences.expected_ctc = Number(totalExpectedCompensation);
        const jobId = tab === '2' ? data?._id : data?.job_id;
        const jobDetails = tab === '2' ? data : data?.jobDetails;
        const response = await setApplication({
          candidateDetails,
          matchingSkills,
          jobId,
          jobDetails,
          status: 'applied',
          resumeData: resumeSelected,
          tj_score: data?.tj_score,
          answers,
        });

        if (response?.success && response?.data != null) {
          const company = response?.data?.job_id?.company;
          const title = response?.data?.job_id?.title;
          enqueueSnackbar(`Successfully applied to ${toTitleCase(company)} for the ${title} position`, { variant: 'success' });
          setJobApply(null);
          if (tab === '3') {
            data = response?.data;
          } else {
            data.application_status = response?.data?.status;
          }
          onClose();
          onSelected(resumeSelected);
        } else {
          enqueueSnackbar(response?.message, { variant: 'error' });
        }
      } else {
        enqueueSnackbar('Failed to submit the application. Please try again.', { variant: 'error' });
      }
    } catch (error) {
      console.error('Error submitting application:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * Handles the skip question action.
   * Sets the isSkipClicked state to true and sets the answer to an empty string.
   */
  const handleSkipQuestion = () => {
    setIsSkipClicked(true);
    handleSetAnswer('');
  };

  /**
   * Updates the current answer and index for editing a question.
   * If the text field is not already shown, this function will display it.
   * 
   * @param {number} index - The index of the question being edited.
   * @param {string} answer - The new answer to update for the question.
   */

  const handleUpdateAnswer = (index, answer) => {
    setEditQusIndex(index);
    setCurrentAnswer(answer);
    setShowTextField(true);
  };

  const handleOpenSelectResumeDialog = () => {
    openDialog('updateSelectResumeDialog');
  };
  const handleCloseSelectResumeDialog = () => {
    closeDialog('updateSelectResumeDialog');
  };

  const commonProps = {
    fetchResumeData: fetchData,
    onSelect: handleSelectResumeFromDialog,
    resumeData,
    tab,
    job: data,
    selectedResume: resumeSelected,
    index: sliderIndex,
    answers,
    chosenResume,
    messages,
    candidateDetails,
    totalExpectedCompensation,
    disableUpdate,
    currentFixed,
    currentVariable,
    currentPerks,
    expectedHourlyRate,
    totalCurrentCTC: totalCompensation
  };
  const chatResumeProps = {
    fetchResumeData: fetchData,
    onSet: handleSelectResumeInChat,
    resumeSelected,
    resumeData,
    tab,
    job: data,
    answers,
    messages,
    totalExpectedCompensation,
    disableUpdate,
    candidateDetails,
    currentFixed,
    currentVariable,
    currentPerks,
    expectedHourlyRate,
    totalCurrentCTC: totalCompensation
  };

  return (
    <Box sx={{ width: { xs: '100%', md: '450px' } }} className="job-app-drawer-container chat-bot-drawer-container" role="presentation">
      <Box className="drawer-header">
        <Stack direction="row" alignItems={'flex-start'} spacing={1}>
          <IconButton aria-label="delete" onClick={onClose} sx={{ paddingTop: 0, paddingBottom: 0 }}>
            <SvgIcon>
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path
                  d="M6.22796 5.04477C5.90134 4.71815 5.37178 4.71815 5.04516 5.04477C4.71854 5.37139 4.71854 5.90095 5.04516 6.22757L10.8174 11.9998L5.04516 17.772C4.71854 18.0987 4.71854 18.6282 5.04516 18.9548C5.37178 19.2815 5.90134 19.2815 6.22796 18.9548L12.0002 13.1826L17.7724 18.9548C18.0991 19.2815 18.6286 19.2815 18.9552 18.9548C19.2819 18.6282 19.2819 18.0987 18.9552 17.772L13.183 11.9998L18.9552 6.22757C19.2819 5.90095 19.2819 5.37139 18.9552 5.04477C18.6286 4.71815 18.0991 4.71815 17.7724 5.04477L12.0002 10.817L6.22796 5.04477Z"
                  fill="white"
                />
              </svg>
            </SvgIcon>
          </IconButton>

          <Box>
            <Typography className="title">Job Application</Typography>
            <Typography className="sub-text">
              Please help us with answers to few questions to complete your application
            </Typography>
          </Box>
        </Stack>
      </Box>

      <Box
        className="drawer-content"
        sx={{
          height: (() => {
            const isFooterVisible = ((showQuestions || editQusIndex !== null)) ||
              (!isPartTimeJob && !totalExpectedCompensation && !resumeSelected && !activeUpdateForm && !isSubmittingCTC);
            if (isFooterVisible) {
              if (isMobile) return '80vh !important'
              else return '85vh !important';
            }
            return 0;
          })(),
        }}
      >
        <Box className="chat-content-container" ref={scrollRef}>
          <Box className="chat-inner-content">
            <Stack spacing={2}>
              <Box className="chat-row message-in">
                <Stack direction="row" spacing={1}>
                  <SvgIcon className="jn-chat-icon">
                    <svg xmlns="http://www.w3.org/2000/svg" width="26" height="28" viewBox="0 0 26 28" fill="none">
                      <path
                        d="M10.4 25.8375L12.1555 26.809C12.7873 27.1737 13.5565 27.1545 14.1824 26.7999L18.396 24.3892C18.4641 24.3443 18.5504 24.2677 18.6184 24.2228L24.0025 21.098C24.6284 20.7434 25.0296 20.0484 25.0237 19.3291L25.0116 7.82422C25.0058 7.10486 24.6062 6.41089 23.9745 6.04615L16.046 1.46864L16.0144 1.45041L14.0244 0.301469C13.3926 -0.0632728 12.5919 -0.0623187 11.966 0.2923L1.9964 6.03424C1.37049 6.38886 0.969277 7.08379 0.975122 7.80315L0.966118 16.9373C0.969695 17.1499 0.991509 17.331 1.04491 17.5303L1.05041 19.3445C1.05625 20.0638 1.45581 20.7578 2.08756 21.1226L9.98445 25.6818C10.129 25.7232 10.2554 25.7961 10.4 25.8375Z"
                        fill="#3D1C9B"
                      />
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M8.09204 14.572L6.79785 16.1014C7.38612 16.7838 8.30381 17.1602 9.43328 17.1367C11.3157 17.1367 12.3511 16.1955 12.3511 14.2426V9.88965H9.99802V11.7014V14.4073C9.99802 14.972 9.71565 15.2779 9.22151 15.2779C8.82149 15.2779 8.46853 15.0426 8.09204 14.572ZM19.1283 17.0209V13.8679C19.1283 12.1267 18.14 11.3738 16.8693 11.3738C16.2105 11.3738 15.6457 11.5856 15.1987 12.0326V11.4914H13.0338V16.9973H15.3163V14.4091C15.3163 13.5856 15.6928 13.3032 16.1634 13.3032C16.587 13.3032 16.8458 13.562 16.8458 14.2444V17.0209H19.1283Z"
                        fill="white"
                      />
                    </svg>
                  </SvgIcon>
                  <Box className="message-in-text-container">
                    <Typography className="msg-text">Great! Please proceed with the application for <Typography variant="caption" sx={{ fontWeight: 700 }}>{data.role}</Typography> at <Typography variant="caption" sx={{ fontWeight: 700 }}>{data.company}</Typography></Typography>
                  </Box>
                </Stack>
              </Box>
              <Box className="chat-row message-in">
                <div className="chat-container">
                  {messages.map((message) => (
                    <div key={message.id}
                      id={`message-${message.id}`}
                    >
                      {isPartTimeJob ? (
                        <>
                          {(message.type === 'p-initial' || message.type === 'updated-p-options') && renderPartTime(message?.snapshot, message)}

                          {message.type === 'partTime-update-request' && (
                            <Box className="chat-row message-out" sx={{ mt: 2 }}>
                              <Stack direction="row" justifyContent={'flex-end'} spacing={1}>
                                <Box className="message-out-text-container">
                                  <Stack spacing={1} sx={{ flex: '0 0 100%' }}>
                                    <Box className="selected-resume-wrapper">
                                      <Stack mb={1} spacing={0.5}>
                                        <Typography className="msg-text">
                                          Your Expected Hourly Rate
                                        </Typography>
                                        <Typography className="msg-text ctc-amt">
                                          ₹{formatCurrency(message?.hourlyRate)} / hour
                                        </Typography>

                                      </Stack>
                                    </Box>
                                    <Typography className="msg-text">
                                      Update Expected Hourly Rate
                                    </Typography>
                                  </Stack>
                                </Box>
                              </Stack>
                            </Box>
                          )}

                          {message.type === 'partTime-confirmation' && (
                            <Box className="chat-row message-in" sx={{ mt: 2 }}>
                              <Stack direction="row" spacing={1}>
                                <SvgIcon className="jn-chat-icon">
                                  <JobNetworkIcon />
                                </SvgIcon>
                                <Box className="message-in-text-container">
                                  <Typography className="msg-text">
                                    Sure, please update your Expected Hourly Rate below!
                                  </Typography>
                                </Box>
                              </Stack>
                            </Box>
                          )}

                          {message.type === 'partTime-update-form' && (
                            <Box className="chat-row message-in" sx={{ mt: 2 }}>
                              <Stack direction="row" spacing={1}>
                                <SvgIcon className="jn-chat-icon">
                                  <JobNetworkIcon />
                                </SvgIcon>
                                <Box className="message-in-text-container">
                                  <Typography className="msg-text" sx={{ fontWeight: 400 }}>
                                    Your <Box component="span" sx={{ fontWeight: 700, display: 'inline' }}>Expected Hourly Rate</Box>
                                  </Typography>
                                  <Stack mb={1} spacing={1.5}>
                                    <TextField
                                      id="hourly-rate"
                                      variant="outlined"
                                      label="Expected Hourly Rate"
                                      type="text"
                                      disabled={!message.updateInProgress}
                                      value={
                                        !message.updateInProgress
                                          ? message?.hourlyRate
                                          : focusedInput === 'hourly-rate'
                                            ? inputValue
                                            : (expectedHourlyRate && !isNaN(expectedHourlyRate))
                                              ? Number(expectedHourlyRate).toLocaleString('en-IN')
                                              : ''
                                      }
                                      onChange={handleHourlyRateChange}
                                      onInput={(e) => {
                                        e.target.value = e.target.value.replace(/[^\d]/g, '').slice(0, 6);
                                      }}
                                      onFocus={(e) => {
                                        setFocusedInput(e.target.id);
                                        setInputValue(expectedHourlyRate.toString());
                                      }}
                                      onBlur={() => {
                                        setFocusedInput(null);
                                        const parsed = parseInt(inputValue, 10);
                                        if (!isNaN(parsed)) {
                                          setExpectedHourlyRate(parsed);
                                        } else {
                                          setExpectedHourlyRate('');
                                          setInputValue('');
                                        }
                                      }}
                                      error={isHourlyRateError}
                                      helperText={isHourlyRateError ? hourlyRateHelperText : ''}
                                      inputProps={{
                                        inputMode: 'numeric',
                                        pattern: '[0-9]*',
                                        maxLength: 6,
                                      }}
                                      InputProps={{
                                        startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                                        endAdornment: (
                                          (message.updateInProgress ? inputValue : expectedHourlyRate.toString()).length > 0 ? (
                                            <InputAdornment position="end">
                                              <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary', whiteSpace: 'nowrap' }}>
                                                per hour
                                              </Typography>
                                            </InputAdornment>
                                          ) : null
                                        ),
                                      }}
                                      InputLabelProps={{
                                        shrink: (message.updateInProgress ? inputValue : expectedHourlyRate.toString()).length > 0,
                                      }}
                                      fullWidth
                                      sx={{
                                        backgroundColor: 'white',
                                        '& .MuiInputBase-input': {
                                          padding: '14px 0px',
                                          fontSize: '0.9rem',
                                        },
                                        '& .MuiInputLabel-root': {
                                          fontSize: '0.79rem',
                                          transform: 'translate(30px, 14px)',
                                        },
                                        '& .MuiInputLabel-shrink': {
                                          transform: 'translate(14px, -9px) scale(0.75)',
                                        },
                                        width: isMobile ? '22ch' : '32ch',
                                      }}
                                    />
                                  </Stack>
                                </Box>
                              </Stack>
                              <Button
                                className="chat-bot-submit-app-btn"
                                variant="contained"
                                size="large"
                                sx={{
                                  width: '100px!important',
                                  opacity: inputValue.trim() ? 1 : 0.5,
                                  mt: 1
                                }}
                                onClick={handlePartTimeSubmit}
                                disabled={!message.updateInProgress || !inputValue.trim()}
                                disableRipple
                                disableElevation
                              >
                                Update
                              </Button>
                            </Box>
                          )}


                          {(message.type === 'p-initial' || message.type === 'updated-p-options') &&

                            <Button
                              className="chat-bot-continue-app-btn"
                              variant="outlined"
                              size="large"
                              onClick={handlePartTimeProceed}
                              sx={{ mt: 2 }}
                              disabled={resumeSelected || !message.continue || partTimeContinueFlag}
                            >
                              Continue With Application
                            </Button>}

                          {message.type === 'confirmation-part-time' && !updateInProgress && message.id === messages[messages.length - 1]?.id && (<>
                            <Box className="chat-row message-out" sx={{ mt: 2 }}>
                              <Stack direction="row" justifyContent={'flex-end'} spacing={1}>
                                <Box className="message-out-text-container">
                                  <Stack spacing={1} sx={{ flex: '0 0 100%' }}>
                                    <Box className="selected-resume-wrapper">
                                      <Stack mb={1} spacing={0.5}>
                                        <Typography className="msg-text">Your Expected Hourly Rate</Typography>
                                        <Typography className="msg-text ctc-amt">₹{formatCurrency(message?.snapshot?.hourlyRate)} / Hour</Typography>
                                      </Stack>
                                    </Box>
                                    <Typography className="msg-text">Continue with Application</Typography>
                                  </Stack>
                                </Box>
                              </Stack>
                            </Box>
                            {!updateInProgress && (<>
                              <Box className="chat-row message-in" sx={{ mt: 2 }}>
                                <Stack direction="row" spacing={1}>
                                  <SvgIcon className="jn-chat-icon">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="26" height="28" viewBox="0 0 26 28" fill="none">
                                      <path d="M10.4 25.8375L12.1555 26.809C12.7873 27.1737 13.5565 27.1545 14.1824 26.7999L18.396 24.3892C18.4641 24.3443 18.5504 24.2677 18.6184 24.2228L24.0025 21.098C24.6284 20.7434 25.0296 20.0484 25.0237 19.3291L25.0116 7.82422C25.0058 7.10486 24.6062 6.41089 23.9745 6.04615L16.046 1.46864L16.0144 1.45041L14.0244 0.301469C13.3926 -0.0632728 12.5919 -0.0623187 11.966 0.2923L1.9964 6.03424C1.37049 6.38886 0.969277 7.08379 0.975122 7.80315L0.966118 16.9373C0.969695 17.1499 0.991509 17.331 1.04491 17.5303L1.05041 19.3445C1.05625 20.0638 1.45581 20.7578 2.08756 21.1226L9.98445 25.6818C10.129 25.7232 10.2554 25.7961 10.4 25.8375Z" fill="#3D1C9B" />
                                      <path fill-rule="evenodd" clip-rule="evenodd" d="M8.09204 14.572L6.79785 16.1014C7.38612 16.7838 8.30381 17.1602 9.43328 17.1367C11.3157 17.1367 12.3511 16.1955 12.3511 14.2426V9.88965H9.99802V11.7014V14.4073C9.99802 14.972 9.71565 15.2779 9.22151 15.2779C8.82149 15.2779 8.46853 15.0426 8.09204 14.572ZM19.1283 17.0209V13.8679C19.1283 12.1267 18.14 11.3738 16.8693 11.3738C16.2105 11.3738 15.6457 11.5856 15.1987 12.0326V11.4914H13.0338V16.9973H15.3163V14.4091C15.3163 13.5856 15.6928 13.3032 16.1634 13.3032C16.587 13.3032 16.8458 13.562 16.8458 14.2444V17.0209H19.1283Z" fill="white" />
                                    </svg>
                                  </SvgIcon>
                                  <Box className="message-in-text-container"
                                  >
                                    <Typography mb={1} className="msg-text">Which resume would you like to apply with?</Typography>

                                    {resumeData?.length > 0 && (
                                      <ChatResumeOptions  {...chatResumeProps} />
                                    )}
                                  </Box>
                                </Stack>
                              </Box>
                            </>)}
                          </>
                          )}

                        </>

                      ) : (<>
                        {(message.type === 'initial' || message.type === 'updated-options') && renderOptionsView(message?.snapshot, message)}

                        {message.type === 'user-expected-ctc' && (
                          <Box className="chat-row message-out" sx={{ mt: 2 }}>
                            <Stack direction="row" justifyContent={'flex-end'} spacing={1}>
                              <Box className="edit-ans">
                                <Box className="message-out-text-container">
                                  <Stack direction="row" justifyContent={'flex-end'} spacing={1}>
                                    <Typography className="msg-text">₹{message.value}</Typography>
                                  </Stack>
                                </Box>
                              </Box>
                            </Stack>
                          </Box>
                        )}
                        {/* User update request message */}
                        {message.type === 'user-update-request' && (
                          <Box className="chat-row message-out" sx={{ mt: 2 }}>
                            <Stack direction="row" justifyContent={'flex-end'} spacing={1}>
                              <Box className="message-out-text-container">
                                <Stack spacing={1} sx={{ flex: '0 0 100%' }}>
                                  <Box className="selected-resume-wrapper">
                                    <Stack mb={1} spacing={0.5}>
                                      <Typography className="msg-text">
                                        Your {message.updateType === 'current' ? 'Current' : 'Expected'} CTC (Fixed + Variable)
                                      </Typography>
                                      <Typography className="msg-text ctc-amt">
                                        ₹{formatCurrency(message.updateType === 'current' ? message.snapshot?.totalCurrentCTC : message.snapshot?.totalExpectedCTC)}
                                      </Typography>
                                      {message.updateType === 'current' && message?.snapshot?.perks && (
                                        <Typography className="msg-text" sx={{ color: '#6B7280!important', lineHeight: '18px' }}>
                                          {`Perks- ${message?.snapshot?.perks}`}
                                        </Typography>
                                      )}
                                    </Stack>
                                  </Box>
                                  <Typography className="msg-text">
                                    Update {message.updateType === 'current' ? 'Current' : 'Expected'} CTC
                                  </Typography>
                                </Stack>
                              </Box>
                            </Stack>
                          </Box>
                        )}

                        {/* Bot confirmation message */}
                        {message.type === 'bot-confirmation' && (
                          <Box className="chat-row message-in" sx={{ mt: 2 }}>
                            <Stack direction="row" spacing={1}>
                              <SvgIcon className="jn-chat-icon">
                                <JobNetworkIcon />
                              </SvgIcon>
                              <Box className="message-in-text-container">
                                <Typography className="msg-text">
                                  Sure, please update your {message.updateType === 'current' ? 'Current' : 'Expected'} CTC below.
                                </Typography>
                              </Box>
                            </Stack>
                          </Box>
                        )}

                        {/* Update form */}
                        {message.type === 'update-form' && (
                          <Box className="chat-row message-in" sx={{ mt: 2 }}>
                            <Stack direction="row" spacing={1}>
                              <SvgIcon className="jn-chat-icon">
                                <JobNetworkIcon />
                              </SvgIcon>
                              <Box className="message-in-text-container">
                                <Typography className="msg-text" sx={{ fontWeight: 400 }}>
                                  Your updated {message.updateType === "current" ? "Current" : "Expected"} CTC
                                </Typography>
                                <Stack mb={1} spacing={1.5}>
                                  {(() => {
                                    const isCurrentFormType = message.updateType === "current";
                                    const isActiveForm = message.updateInProgress;
                                    const fixedValue = isActiveForm ? (isCurrentFormType ? formCurrentFixed : formExpectedFixed) : message.snapshot?.fixed;
                                    const variableValue = isActiveForm ? formCurrentVariable : message.snapshot?.variable;
                                    const perksValue = isActiveForm ? formCurrentPerks : message.snapshot?.perks;

                                    const setFixedValue = isCurrentFormType ? setFormCurrentFixed : setFormExpectedFixed;

                                    return (
                                      <>
                                        <TextField
                                          id={`fixed-compensation-${message.id}`}
                                          label="Annual Fixed Compensation"
                                          variant="outlined"
                                          fullWidth
                                          type="text"
                                          disabled={!isActiveForm}
                                          value={
                                            focusedInput === `fixed-compensation-${message.id}`
                                              ? fixedValue
                                              : (fixedValue ? Number(fixedValue).toLocaleString('en-IN') : '')
                                          }
                                          onInput={(e) => {
                                            e.target.value = e.target.value.replace(/[^\d]/g, '').slice(0, 9);
                                          }}
                                          onChange={(e) => {
                                            const value = e.target.value.replace(/,/g, '');
                                            if (/^\d*$/.test(value)) {
                                              setFixedValue(value);
                                              if (isFixedError) {
                                                setIsFixedError(false);
                                                setFixedHelperText('');
                                              }
                                            }
                                          }}
                                          onFocus={(e) => setFocusedInput(e.target.id)}
                                          onBlur={() => setFocusedInput(null)}
                                          error={isActiveForm && isFixedError}
                                          helperText={isActiveForm && isFixedError ? fixedHelperText : ''}
                                          InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
                                          InputLabelProps={{ shrink: !!fixedValue }}
                                          inputProps={{
                                            inputMode: 'numeric',
                                            pattern: '[0-9]*',
                                            maxLength: 9,
                                          }}
                                          sx={{
                                            backgroundColor: 'white',
                                            '& .MuiInputBase-input': {
                                              padding: '14px 0px',
                                              fontSize: '0.9rem',
                                            },
                                            '& .MuiInputLabel-root': {
                                              fontSize: '0.79rem',
                                              transform: 'translate(30px, 14px)',
                                            },
                                            '& .MuiInputLabel-shrink': {
                                              transform: 'translate(14px, -9px) scale(0.75)',
                                            },
                                            width: isMobile ? '22ch' : '32ch',
                                          }}
                                        />
                                        {isCurrentFormType && (
                                          <>
                                            <TextField
                                              id={`variable-compensation-${message.id}`}
                                              label="Annual Variable Compensation (Optional)"
                                              type="text"
                                              disabled={!isActiveForm}
                                              value={
                                                focusedInput === `variable-compensation-${message.id}`
                                                  ? variableValue
                                                  : (variableValue ? Number(variableValue).toLocaleString('en-IN') : '')
                                              }
                                              onChange={(e) => {
                                                const value = e.target.value.replace(/,/g, '');
                                                if (/^\d*$/.test(value)) setFormCurrentVariable(value);
                                              }}
                                              onInput={(e) => {
                                                e.target.value = e.target.value.replace(/[^\d]/g, '').slice(0, 9);
                                              }}
                                              onFocus={(e) => setFocusedInput(e.target.id)}
                                              onBlur={() => setFocusedInput(null)}
                                              InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
                                              InputLabelProps={{ shrink: !!variableValue }}
                                              inputProps={{
                                                inputMode: 'numeric',
                                                pattern: '[0-9]*',
                                                maxLength: 9,
                                              }}
                                              sx={{
                                                backgroundColor: 'white',
                                                '& .MuiInputBase-input': {
                                                  padding: '14px 0px',
                                                  fontSize: '0.9rem',
                                                },
                                                '& .MuiInputLabel-root': {
                                                  fontSize: '0.79rem',
                                                  transform: 'translate(30px, 14px)',
                                                },
                                                '& .MuiInputLabel-shrink': {
                                                  transform: 'translate(14px, -9px) scale(0.75)',
                                                },
                                                width: isMobile ? '22ch' : '32ch',
                                              }}
                                            />
                                            <TextField
                                              id={`other-perks-${message.id}`}
                                              label="Other significant perks (Optional)"
                                              type="text"
                                              disabled={!isActiveForm}
                                              value={perksValue || ''}
                                              onChange={(e) => setFormCurrentPerks(e.target.value)}
                                              InputLabelProps={{ shrink: !!perksValue }}
                                              sx={{
                                                backgroundColor: 'white',
                                                '& .MuiInputBase-input': {
                                                  padding: '14px 14px',
                                                  fontSize: '0.9rem',
                                                },
                                                '& .MuiInputLabel-root': {
                                                  fontSize: '0.79rem',
                                                  transform: 'translate(14px, 14px)',
                                                },
                                                '& .MuiInputLabel-shrink': {
                                                  transform: 'translate(14px, -9px) scale(0.75)',
                                                },
                                                width: isMobile ? '22ch' : '32ch',
                                              }}
                                            />
                                          </>
                                        )}
                                      </>
                                    );
                                  })()}
                                </Stack>
                              </Box>
                            </Stack>
                            {message.updateInProgress && (
                              <Button
                                className="chat-bot-submit-app-btn"
                                variant="contained"
                                size="large"
                                sx={{
                                  width: '100px!important',
                                  opacity: (activeUpdateForm === 'current' ? formCurrentFixed : formExpectedFixed)?.trim() ? 1 : 0.5,
                                  mt: 1
                                }}
                                onClick={handleUpdateSubmit}
                                disabled={!message.updateInProgress || !(activeUpdateForm === 'current' ? formCurrentFixed : formExpectedFixed)?.trim()}
                                disableRipple
                                disableElevation
                              >
                                Update
                              </Button>
                            )}
                          </Box>
                        )}
                        {(message.type === 'initial' || message.type === 'updated-options') && !!message?.snapshot?.totalExpectedCTC &&
                          <Button
                            className="chat-bot-continue-app-btn"
                            variant="outlined"
                            size="large"
                            onClick={() => handleProceed(message?.snapshot?.totalExpectedCTC)}
                            sx={{ mt: 2 }}
                            disabled={resumeSelected || !message.continue || continueFlag}
                          >
                            Continue With Application
                          </Button>}
                        {message.type === 'confirmation' && !updateInProgress && message.id === messages[messages.length - 1]?.id && (<>
                          <Box className="chat-row message-out" sx={{ mt: 2 }}>
                            <Stack direction="row" justifyContent={'flex-end'} spacing={1}>
                              <Box className="message-out-text-container">
                                <Stack spacing={1} sx={{ flex: '0 0 100%' }}>
                                  <Box className="selected-resume-wrapper">
                                    <Stack mb={1} spacing={0.5}>
                                      <Typography className="msg-text">Your Current CTC</Typography>
                                      <Typography className="msg-text ctc-amt">₹{formatCurrency(message?.snapshot?.current)}</Typography>
                                      {message?.snapshot?.perks && <Typography className="msg-text" sx={{ color: '#6B7280!important', lineHeight: '18px' }}>
                                        (Including variable component and {message.snapshot.perks} additional perks)
                                      </Typography>}
                                    </Stack>
                                    <Stack mb={1} spacing={0.5}>
                                      <Typography className="msg-text">Your Expected CTC</Typography>
                                      <Typography className="msg-text ctc-amt">₹{formatCurrency(message?.snapshot?.expected)}</Typography>
                                    </Stack>
                                  </Box>
                                  <Typography className="msg-text">Continue with Application</Typography>
                                </Stack>
                              </Box>
                            </Stack>
                          </Box>
                          {!updateInProgress && (<>

                            <Box className="chat-row message-in" sx={{ mt: 2 }}>
                              <Stack direction="row" spacing={1}>
                                <SvgIcon className="jn-chat-icon">
                                  <svg xmlns="http://www.w3.org/2000/svg" width="26" height="28" viewBox="0 0 26 28" fill="none">
                                    <path d="M10.4 25.8375L12.1555 26.809C12.7873 27.1737 13.5565 27.1545 14.1824 26.7999L18.396 24.3892C18.4641 24.3443 18.5504 24.2677 18.6184 24.2228L24.0025 21.098C24.6284 20.7434 25.0296 20.0484 25.0237 19.3291L25.0116 7.82422C25.0058 7.10486 24.6062 6.41089 23.9745 6.04615L16.046 1.46864L16.0144 1.45041L14.0244 0.301469C13.3926 -0.0632728 12.5919 -0.0623187 11.966 0.2923L1.9964 6.03424C1.37049 6.38886 0.969277 7.08379 0.975122 7.80315L0.966118 16.9373C0.969695 17.1499 0.991509 17.331 1.04491 17.5303L1.05041 19.3445C1.05625 20.0638 1.45581 20.7578 2.08756 21.1226L9.98445 25.6818C10.129 25.7232 10.2554 25.7961 10.4 25.8375Z" fill="#3D1C9B" />
                                    <path fill-rule="evenodd" clip-rule="evenodd" d="M8.09204 14.572L6.79785 16.1014C7.38612 16.7838 8.30381 17.1602 9.43328 17.1367C11.3157 17.1367 12.3511 16.1955 12.3511 14.2426V9.88965H9.99802V11.7014V14.4073C9.99802 14.972 9.71565 15.2779 9.22151 15.2779C8.82149 15.2779 8.46853 15.0426 8.09204 14.572ZM19.1283 17.0209V13.8679C19.1283 12.1267 18.14 11.3738 16.8693 11.3738C16.2105 11.3738 15.6457 11.5856 15.1987 12.0326V11.4914H13.0338V16.9973H15.3163V14.4091C15.3163 13.5856 15.6928 13.3032 16.1634 13.3032C16.587 13.3032 16.8458 13.562 16.8458 14.2444V17.0209H19.1283Z" fill="white" />
                                  </svg>
                                </SvgIcon>
                                <Box className="message-in-text-container"
                                >
                                  <Typography mb={1} className="msg-text">Which resume would you like to apply with?</Typography>

                                  {resumeData?.length > 0 && (
                                    <ChatResumeOptions  {...chatResumeProps} />
                                  )}
                                </Box>
                              </Stack>
                            </Box>
                          </>)}
                        </>
                        )}
                      </>)}
                    </div >
                  ))}
                </div>
              </Box>

              {resumeSelected && (
                <Box className="chat-row message-out">
                  <Stack direction="row" justifyContent={'flex-end'} spacing={1}>
                    <Box className="message-out-text-container">
                      <Stack spacing={1} sx={{ flex: '0 0 100%' }}>
                        <Box className="selected-resume-wrapper">
                          <Box className="resume-img">
                            <Button
                              className="change-resume-btn"
                              variant="outlined"
                              size="large"
                              disableRipple
                              disableElevation
                              onClick={handleChangeResume}
                            >
                              Change Resume
                            </Button>
                            <img alt="resume" src={resumeImg} />
                          </Box>
                        </Box>
                        <Typography className="msg-text">{resumeSelected?.link ? 'Uploaded Resume' : resumeSelected?.title}</Typography>
                      </Stack>
                    </Box>
                  </Stack>
                  {questions && questions?.length > 0 && !answers && (
                    <Stack spacing={2} mt={2}>
                      <Box className="chat-row message-in">
                        <Stack direction="row" spacing={1}>
                          <SvgIcon className="jn-chat-icon">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="26"
                              height="28"
                              viewBox="0 0 26 28"
                              fill="none"
                            >
                              <path
                                d="M10.4 25.8375L12.1555 26.809C12.7873 27.1737 13.5565 27.1545 14.1824 26.7999L18.396 24.3892C18.4641 24.3443 18.5504 24.2677 18.6184 24.2228L24.0025 21.098C24.6284 20.7434 25.0296 20.0484 25.0237 19.3291L25.0116 7.82422C25.0058 7.10486 24.6062 6.41089 23.9745 6.04615L16.046 1.46864L16.0144 1.45041L14.0244 0.301469C13.3926 -0.0632728 12.5919 -0.0623187 11.966 0.2923L1.9964 6.03424C1.37049 6.38886 0.969277 7.08379 0.975122 7.80315L0.966118 16.9373C0.969695 17.1499 0.991509 17.331 1.04491 17.5303L1.05041 19.3445C1.05625 20.0638 1.45581 20.7578 2.08756 21.1226L9.98445 25.6818C10.129 25.7232 10.2554 25.7961 10.4 25.8375Z"
                                fill="#3D1C9B"
                              />
                              <path
                                fillRule="evenodd"
                                clipRule="evenodd"
                                d="M8.09204 14.572L6.79785 16.1014C7.38612 16.7838 8.30381 17.1602 9.43328 17.1367C11.3157 17.1367 12.3511 16.1955 12.3511 14.2426V9.88965H9.99802V11.7014V14.4073C9.99802 14.972 9.71565 15.2779 9.22151 15.2779C8.82149 15.2779 8.46853 15.0426 8.09204 14.572ZM19.1283 17.0209V13.8679C19.1283 12.1267 18.14 11.3738 16.8693 11.3738C16.2105 11.3738 15.6457 11.5856 15.1987 12.0326V11.4914H13.0338V16.9973H15.3163V14.4091C15.3163 13.5856 15.6928 13.3032 16.1634 13.3032C16.587 13.3032 16.8458 13.562 16.8458 14.2444V17.0209H19.1283Z"
                                fill="white"
                              />
                            </svg>
                          </SvgIcon>
                          <Box className="message-in-text-container">
                            <Typography className="msg-text">
                              Great! Please help us with a few answers to complete the application
                            </Typography>
                          </Box>
                        </Stack>
                      </Box>
                    </Stack>
                  )}
                </Box>
              )}

              {answers && Object.keys(answers).length > 0 && (
                <Box>
                  <Stack spacing={2}>
                    {Object.entries(answers).map(([key, value], index) => (
                      <Box key={index}>
                        <Stack spacing={2}>
                          <Box className="chat-row message-in">
                            <Stack direction="row" spacing={1}>
                              <SvgIcon className="jn-chat-icon">
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="26"
                                  height="28"
                                  viewBox="0 0 26 28"
                                  fill="none"
                                >
                                  <path
                                    d="M10.4 25.8375L12.1555 26.809C12.7873 27.1737 13.5565 27.1545 14.1824 26.7999L18.396 24.3892C18.4641 24.3443 18.5504 24.2677 18.6184 24.2228L24.0025 21.098C24.6284 20.7434 25.0296 20.0484 25.0237 19.3291L25.0116 7.82422C25.0058 7.10486 24.6062 6.41089 23.9745 6.04615L16.046 1.46864L16.0144 1.45041L14.0244 0.301469C13.3926 -0.0632728 12.5919 -0.0623187 11.966 0.2923L1.9964 6.03424C1.37049 6.38886 0.969277 7.08379 0.975122 7.80315L0.966118 16.9373C0.969695 17.1499 0.991509 17.331 1.04491 17.5303L1.05041 19.3445C1.05625 20.0638 1.45581 20.7578 2.08756 21.1226L9.98445 25.6818C10.129 25.7232 10.2554 25.7961 10.4 25.8375Z"
                                    fill="#3D1C9B"
                                  />
                                  <path
                                    fillRule="evenodd"
                                    clipRule="evenodd"
                                    d="M8.09204 14.572L6.79785 16.1014C7.38612 16.7838 8.30381 17.1602 9.43328 17.1367C11.3157 17.1367 12.3511 16.1955 12.3511 14.2426V9.88965H9.99802V11.7014V14.4073C9.99802 14.972 9.71565 15.2779 9.22151 15.2779C8.82149 15.2779 8.46853 15.0426 8.09204 14.572ZM19.1283 17.0209V13.8679C19.1283 12.1267 18.14 11.3738 16.8693 11.3738C16.2105 11.3738 15.6457 11.5856 15.1987 12.0326V11.4914H13.0338V16.9973H15.3163V14.4091C15.3163 13.5856 15.6928 13.3032 16.1634 13.3032C16.587 13.3032 16.8458 13.562 16.8458 14.2444V17.0209H19.1283Z"
                                    fill="white"
                                  />
                                </svg>
                              </SvgIcon>
                              <Box className="message-in-text-container">
                                {index === 0 && (<>
                                  <Typography className="msg-text">
                                    <strong>Your Cover Note</strong>
                                    <br />
                                  </Typography>

                                </>)}
                                <Typography className="msg-text">{key}</Typography>
                                {index === 0 && (
                                  <Link
                                    to="#"
                                    className={"disable-link"}
                                  >
                                    Skip
                                  </Link>
                                )}
                              </Box>
                            </Stack>
                          </Box>
                          {value != '' && (
                            <Box className="chat-row message-out">
                              <Stack direction="row" justifyContent={'flex-end'} spacing={1}>
                                <Box className="edit-ans">
                                  <Box className="message-out-text-container">
                                    <Stack direction="row" justifyContent={'flex-end'} spacing={1}>
                                      <Typography className="msg-text">{value}</Typography>
                                    </Stack>
                                    <IconButton
                                      aria-label="edit"
                                      className="edit-button"
                                      style={{ marginLeft: 'auto' }}
                                      onClick={() => handleUpdateAnswer(index, value)}
                                    >
                                      <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="16"
                                        height="16"
                                        viewBox="0 0 16 16"
                                        fill="none"
                                      >
                                        <path
                                          d="M12.2872 16.0003H2.09789C0.941117 16.0003 0 15.0593 0 13.9024V3.71313C0 2.55635 0.941002 1.61523 2.09789 1.61523H6.76704C7.0982 1.61523 7.36641 1.88345 7.36641 2.2146C7.36641 2.54576 7.0982 2.81398 6.76704 2.81398H2.09789C1.60192 2.81398 1.19884 3.21706 1.19884 3.71303V13.9023C1.19884 14.3983 1.60192 14.8014 2.09789 14.8014H12.2872C12.7832 14.8014 13.1862 14.3983 13.1862 13.9023V9.23319C13.1862 8.90203 13.4545 8.63381 13.7856 8.63381C14.1168 8.63381 14.385 8.90203 14.385 9.23319V13.9023C14.385 15.0591 13.4441 16.0003 12.2872 16.0003Z"
                                          fill="#1F9CD7"
                                        />
                                        <path
                                          d="M4.44141 9.00838L10.9986 2.45117L13.5451 4.99768L6.98792 11.5549L4.44141 9.00838Z"
                                          fill="#1F9CD7"
                                        />
                                        <path
                                          d="M15.7098 1.43961L14.5591 0.28882C14.174 -0.0962733 13.5491 -0.0962733 13.1633 0.28882L11.8477 1.60444L14.3942 4.15095L15.7098 2.83533C16.0949 2.45024 16.0949 1.82545 15.7098 1.43961Z"
                                          fill="#1F9CD7"
                                        />
                                        <path
                                          d="M3.67143 9.9353L3.07881 12.523C3.02487 12.7598 3.23615 12.9711 3.4729 12.9171L6.06065 12.3245L3.67143 9.9353Z"
                                          fill="#1F9CD7"
                                        />
                                      </svg>
                                    </IconButton>
                                  </Box>
                                </Box>
                              </Stack>
                            </Box>)}
                        </Stack>
                      </Box>
                    ))}
                  </Stack>
                </Box>
              )}

              {showQuestions && (
                <Box className="chat-row message-in">
                  <Stack direction="row" spacing={1}>
                    <SvgIcon className="jn-chat-icon">
                      <svg xmlns="http://www.w3.org/2000/svg" width="26" height="28" viewBox="0 0 26 28" fill="none">
                        <path
                          d="M10.4 25.8375L12.1555 26.809C12.7873 27.1737 13.5565 27.1545 14.1824 26.7999L18.396 24.3892C18.4641 24.3443 18.5504 24.2677 18.6184 24.2228L24.0025 21.098C24.6284 20.7434 25.0296 20.0484 25.0237 19.3291L25.0116 7.82422C25.0058 7.10486 24.6062 6.41089 23.9745 6.04615L16.046 1.46864L16.0144 1.45041L14.0244 0.301469C13.3926 -0.0632728 12.5919 -0.0623187 11.966 0.2923L1.9964 6.03424C1.37049 6.38886 0.969277 7.08379 0.975122 7.80315L0.966118 16.9373C0.969695 17.1499 0.991509 17.331 1.04491 17.5303L1.05041 19.3445C1.05625 20.0638 1.45581 20.7578 2.08756 21.1226L9.98445 25.6818C10.129 25.7232 10.2554 25.7961 10.4 25.8375Z"
                          fill="#3D1C9B"
                        />
                        <path
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M8.09204 14.572L6.79785 16.1014C7.38612 16.7838 8.30381 17.1602 9.43328 17.1367C11.3157 17.1367 12.3511 16.1955 12.3511 14.2426V9.88965H9.99802V11.7014V14.4073C9.99802 14.972 9.71565 15.2779 9.22151 15.2779C8.82149 15.2779 8.46853 15.0426 8.09204 14.572ZM19.1283 17.0209V13.8679C19.1283 12.1267 18.14 11.3738 16.8693 11.3738C16.2105 11.3738 15.6457 11.5856 15.1987 12.0326V11.4914H13.0338V16.9973H15.3163V14.4091C15.3163 13.5856 15.6928 13.3032 16.1634 13.3032C16.587 13.3032 16.8458 13.562 16.8458 14.2444V17.0209H19.1283Z"
                          fill="white"
                        />
                      </svg>
                    </SvgIcon>
                    <Box className="message-in-text-container">
                      <Typography className="msg-text">

                        {currentQuestionIndex === 0 && (
                          <>
                            <strong>Your Cover Note</strong>
                            <br />
                          </>
                        )}
                        {questions[currentQuestionIndex]}
                      </Typography>

                      {currentQuestionIndex === 0 && (
                        <Link
                          to="#"
                          onClick={(e) => {
                            e.preventDefault();
                            setCurrentAnswer('');
                            if (!isSkipClicked) handleSkipQuestion();
                          }}
                          className={isSkipClicked ? "disable-link" : "update-link"}
                        >
                          Skip
                        </Link>
                      )}
                    </Box>
                  </Stack>
                </Box>
              )}
              {chatCompleted && (
                <Box>
                  <Stack spacing={2} mb={2}>
                    <Box className="chat-row message-in">
                      <Stack direction="row" spacing={1}>
                        <SvgIcon className="jn-chat-icon">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="26"
                            height="28"
                            viewBox="0 0 26 28"
                            fill="none"
                          >
                            <path
                              d="M10.4 25.8375L12.1555 26.809C12.7873 27.1737 13.5565 27.1545 14.1824 26.7999L18.396 24.3892C18.4641 24.3443 18.5504 24.2677 18.6184 24.2228L24.0025 21.098C24.6284 20.7434 25.0296 20.0484 25.0237 19.3291L25.0116 7.82422C25.0058 7.10486 24.6062 6.41089 23.9745 6.04615L16.046 1.46864L16.0144 1.45041L14.0244 0.301469C13.3926 -0.0632728 12.5919 -0.0623187 11.966 0.2923L1.9964 6.03424C1.37049 6.38886 0.969277 7.08379 0.975122 7.80315L0.966118 16.9373C0.969695 17.1499 0.991509 17.331 1.04491 17.5303L1.05041 19.3445C1.05625 20.0638 1.45581 20.7578 2.08756 21.1226L9.98445 25.6818C10.129 25.7232 10.2554 25.7961 10.4 25.8375Z"
                              fill="#3D1C9B"
                            />
                            <path
                              fillRule="evenodd"
                              clipRule="evenodd"
                              d="M8.09204 14.572L6.79785 16.1014C7.38612 16.7838 8.30381 17.1602 9.43328 17.1367C11.3157 17.1367 12.3511 16.1955 12.3511 14.2426V9.88965H9.99802V11.7014V14.4073C9.99802 14.972 9.71565 15.2779 9.22151 15.2779C8.82149 15.2779 8.46853 15.0426 8.09204 14.572ZM19.1283 17.0209V13.8679C19.1283 12.1267 18.14 11.3738 16.8693 11.3738C16.2105 11.3738 15.6457 11.5856 15.1987 12.0326V11.4914H13.0338V16.9973H15.3163V14.4091C15.3163 13.5856 15.6928 13.3032 16.1634 13.3032C16.587 13.3032 16.8458 13.562 16.8458 14.2444V17.0209H19.1283Z"
                              fill="white"
                            />
                          </svg>
                        </SvgIcon>
                        <Box className="message-in-text-container">
                          <Stack spacing={1} sx={{ flex: '0 0 100%' }}>
                            <Typography className="msg-text">
                              Great! We’ve received your responses. Click submit to send your application.{' '}
                            </Typography>
                            <Typography className="msg-text">
                              You can review and update your responses by clicking them before submitting the
                              application.
                            </Typography>
                          </Stack>
                        </Box>
                      </Stack>
                    </Box>
                  </Stack>
                  <Button
                    className="chat-bot-submit-app-btn"
                    variant="contained"
                    size="large"
                    onClick={onSubmit}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Submitting...' : 'Submit Application'}
                  </Button>
                </Box>
              )}
            </Stack>
            {loading && <Box className="chat-loader"></Box>}
          </Box>
        </Box>

        {
          (() => {
            // Condition 1: Show input for missing Expected CTC (for full-time jobs)
            const showInputForMissingCTC =
              !isPartTimeJob &&
              !totalExpectedCompensation &&
              !resumeSelected &&
              !activeUpdateForm &&
              !isSubmittingCTC;

            // Condition 2: Show input for answering screener questions (after resume is selected)
            const showInputForQuestions = !!(resumeSelected && (showQuestions || editQusIndex !== null));

            const handleSendClick = () => {
              if (showInputForQuestions) {
                handleSetAnswer(currentAnswer);
              } else if (showInputForMissingCTC) {
                handleSetExpectedCTC(currentAnswer);
              }
            };

            // Render the footer if either condition is met and the flags are true
            if (textFieldFlag && showTextField && (showInputForMissingCTC || showInputForQuestions)) {
              return (
                <Box className="chat-footer">
                  <Box className="chat-footer-cta">
                    <Stack direction="row" alignItems={'center'} spacing={2}>
                      <FormControl fullWidth>
                        <TextField
                          size="small"
                          placeholder={showInputForQuestions ? "Your Answer (Max. 30 words)" : "Your Expected CTC"}
                          variant="outlined"
                          type={showInputForQuestions ? "text" : "number"}
                          multiline={showInputForQuestions}
                          value={currentAnswer}
                          onChange={handleInputChange}
                          InputProps={{
                            startAdornment: (showInputForMissingCTC ? (
                              <InputAdornment position="start">₹</InputAdornment>
                            ) : null)
                          }}
                          inputProps={{
                            maxLength: showInputForQuestions ? undefined : 12,
                            inputMode: showInputForQuestions ? 'text' : 'numeric',
                            pattern: showInputForQuestions ? undefined : '[0-9]*',
                          }}
                        />
                        {(!!totalExpectedCompensation || isPartTimeJob) && (
                          <Typography sx={{ mt: 0.5, fontSize: '0.75rem', color: '#666', textAlign: 'right' }}>
                            {`${currentAnswer.split(/\s+/).filter((word) => word !== '').length} of 30 words`}
                          </Typography>
                        )}
                      </FormControl>

                      <IconButton
                        aria-label="delete"
                        disabled={!currentAnswer.trim()}
                        onClick={handleSendClick}
                        className="send-msg-btn"
                        sx={{ mb: showInputForQuestions ? '20px !important ' : 0 }}
                      >
                        <SvgIcon>
                          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <path
                              d="M8.95019 4.07992L15.4702 10.5999C16.2402 11.3699 16.2402 12.6299 15.4702 13.3999L8.9502 19.9199"
                              stroke="white"
                              strokeWidth="1.5"
                              strokeMiterlimit="10"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </SvgIcon>
                      </IconButton>
                    </Stack>
                  </Box>
                </Box>
              );
            }
            return null;
          })()
        }
      </Box >
      {/* select resume options */}
      {isMobile ?
        <Drawer
          anchor="right"
          open={selectResumeDialogOpen}
          onClose={handleClosed}
          className="mobile-bottomsheet-dialog action-dialog-card external-job-app-dialog"
        >
          <SelectResumeMobile
            {...commonProps}
            onClose={handleClosed}
          />
        </Drawer> :
        <Dialog
          open={selectResumeDialogOpen}
          onClose={handleClosed}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
          className="select-resumes-dialog"
        >
          <SelectResumeOptions {...commonProps} onClose={handleClosed} />
        </Dialog>
      }
    </Box >
  );
};

export default ChatBotDrawer;
